import React, { useState, useRef, useEffect } from 'react';
import {
  ProjectOnboarding,
  OnboardingItem,
  CRMUser,
  ProjectDepartment,
  CUSTOM_FRAME_DEPARTMENTS,
  DEPARTMENT_DISPLAY_LABELS,
} from '../../types';
import { Check, X, ClipboardCheck, ArrowUp, ArrowDown, User, RefreshCw, Loader2 } from 'lucide-react';
import './OnboardingBanner.css';

interface OnboardingModalProps {
  onboarding: ProjectOnboarding;
  crmUsers: CRMUser[];
  onToggleItem: (itemId: string, completed: boolean) => void;
  onChangeAssignee: (itemId: string, assigneeId: string | undefined, assigneeName: string | undefined) => void;
  /** Task group follows department (same mapping as task detail). */
  onChangeDepartment: (itemId: string, department: ProjectDepartment) => void;
  onReorder: (itemId: string, direction: 'up' | 'down') => void;
  onSyncTemplate: () => Promise<number>;
  onClose: () => void;
}

const AVATAR_COLORS = [
  '#2C3E2F', '#3B82F6', '#7C3AED', '#C2410C', '#059669',
  '#9333EA', '#DC2626', '#0891B2', '#CA8A04', '#6366F1',
];
function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
function initials(name: string): string {
  return name.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({
  onboarding, crmUsers, onToggleItem, onChangeAssignee, onChangeDepartment, onReorder, onSyncTemplate, onClose,
}) => {
  const [justChecked, setJustChecked] = useState<string | null>(null);
  const [assigneeDropdownId, setAssigneeDropdownId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const items = [...onboarding.items].sort((a, b) => a.order - b.order);
  const done = items.filter(i => i.completed).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const allDone = onboarding.status === 'completed';

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    if (!assigneeDropdownId) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setAssigneeDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [assigneeDropdownId]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const added = await onSyncTemplate();
      setSyncResult(added > 0 ? `${added} new step${added > 1 ? 's' : ''} added` : 'Already up to date');
      setTimeout(() => setSyncResult(null), 3000);
    } catch {
      setSyncResult('Failed to sync');
      setTimeout(() => setSyncResult(null), 3000);
    } finally {
      setSyncing(false);
    }
  };

  const handleCheck = (item: OnboardingItem) => {
    const val = !item.completed;
    onToggleItem(item.id, val);
    if (val) {
      setJustChecked(item.id);
      setTimeout(() => setJustChecked(null), 300);
    }
  };

  return (
    <div className="ob-overlay" onClick={onClose}>
      <div className={`ob-modal ${allDone ? 'is-complete' : ''}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="ob-modal-header">
          <div className="ob-modal-icon">
            <ClipboardCheck size={16} />
          </div>
          <div className="ob-modal-title-block">
            <div className="ob-modal-title">{onboarding.projectName}</div>
            <div className="ob-modal-subtitle">
              {allDone ? 'All onboarding steps complete' : `${done} of ${total} complete`}
            </div>
          </div>
          <div className="ob-modal-progress-wrap">
            <div className="ob-modal-progress-bar">
              <div className="ob-modal-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="ob-modal-pct">{pct}%</span>
          </div>
          <button
            className={`ob-sync-btn ${syncing ? 'ob-syncing' : ''}`}
            onClick={handleSync}
            disabled={syncing}
            title="Update from default checklist"
          >
            {syncing ? <Loader2 size={13} className="ob-spin-icon" /> : <RefreshCw size={13} />}
            <span>{syncing ? 'Syncing…' : 'Sync Template'}</span>
          </button>
          {syncResult && <span className="ob-sync-result">{syncResult}</span>}
          <button className="ob-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="ob-modal-body">
          {items.map((item, idx) => (
            <div key={item.id} className={`ob-row ${item.completed ? 'is-done' : ''}`}>
              <button
                className={`ob-check ${item.completed ? 'checked' : ''} ${justChecked === item.id ? 'just-popped' : ''}`}
                onClick={() => handleCheck(item)}
              >
                {item.completed && <Check size={13} strokeWidth={3} color="white" />}
              </button>

              <div className="ob-row-content">
                <div className="ob-row-top">
                  <span className="ob-row-title">{item.title}</span>
                  {item.required && <span className="ob-row-required">REQUIRED</span>}
                </div>
                {item.description && <div className="ob-row-desc">{item.description}</div>}
                {item.completed && item.completedByName && (
                  <div className="ob-row-meta">
                    <span className="ob-done-by">
                      {item.completedByName} · {item.completedAt ? new Date(item.completedAt).toLocaleDateString() : ''}
                    </span>
                  </div>
                )}
              </div>

              <div className="ob-row-right">
                <select
                  className="ob-task-group-select"
                  value={item.department || 'design'}
                  onChange={(e) => onChangeDepartment(item.id, e.target.value as ProjectDepartment)}
                  title="Department — task group column matches this"
                >
                  {CUSTOM_FRAME_DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>
                      {DEPARTMENT_DISPLAY_LABELS[d]}
                    </option>
                  ))}
                </select>
                <div style={{ position: 'relative' }} ref={assigneeDropdownId === item.id ? dropdownRef : undefined}>
                  <button
                    className="ob-assignee-btn"
                    onClick={() => setAssigneeDropdownId(assigneeDropdownId === item.id ? null : item.id)}
                  >
                    {item.assigneeId && item.assigneeName ? (
                      <>
                        <span className="ob-avatar-sm" style={{ background: avatarColor(item.assigneeName) }}>
                          {initials(item.assigneeName)}
                        </span>
                        {item.assigneeName.split(' ')[0]}
                      </>
                    ) : (
                      <>
                        <User size={12} style={{ opacity: 0.5 }} />
                        <span style={{ opacity: 0.5 }}>Assign</span>
                      </>
                    )}
                  </button>
                  {assigneeDropdownId === item.id && (
                    <div className="ob-assignee-dropdown" style={{ right: 0, left: 'auto' }}>
                      <button onClick={() => { onChangeAssignee(item.id, undefined, undefined); setAssigneeDropdownId(null); }}>
                        <User size={13} /> Unassigned
                      </button>
                      {crmUsers.filter(u => u.isActive).map(u => (
                        <button key={u.uid} onClick={() => {
                          onChangeAssignee(item.id, u.uid, u.displayName || u.email);
                          setAssigneeDropdownId(null);
                        }}>
                          <span className="ob-avatar-sm" style={{ background: avatarColor(u.displayName || u.email) }}>
                            {initials(u.displayName || u.email)}
                          </span>
                          {u.displayName || u.email}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="ob-reorder-btns">
                  <button className="ob-reorder-btn" disabled={idx === 0} onClick={() => onReorder(item.id, 'up')}>
                    <ArrowUp size={12} />
                  </button>
                  <button className="ob-reorder-btn" disabled={idx === items.length - 1} onClick={() => onReorder(item.id, 'down')}>
                    <ArrowDown size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
